import { api } from "@/src/api/api";
import { endpoints, EndpointType } from "@/src/constants/api";
import networkHelper from "@/src/composables/network-helper";

const { isSuccess } = networkHelper();

// Define types for parameters and return values
/* eslint-disable  @typescript-eslint/no-explicit-any */
export type Payload = Record<string, any>;
export type CustomParams = string[];
export type FormattingParams = Record<string, string>;
export type QueryParams = string;
export type Method = "get" | "post" | "put" | "delete" | "patch";
export type Headers = Record<string, string>;
export type ApiResponse = any; // Replace with your actual response type if available

interface Endpoint {
  url: string;
}

export interface PaginatedPayload {
  limit?: number;
  page?: number;
  search?: string;
  customQuery?: QueryParams;
  customParams?: CustomParams;
  method?: Method;
  payload?: Payload;
  formattingParams?: FormattingParams;
  signal?: AbortSignal;
}

interface NonPaginatedPayload {
  customQuery?: QueryParams;
  customParams?: CustomParams;
  method?: Method;
  search?: string;
  payload?: Payload;
  formattingParams?: FormattingParams;
  signal?: AbortSignal;
}

type UnAuthenticatedCallArgs = {
  payload?: Payload | null;
  customQuery?: QueryParams | null;
  customParams?: CustomParams | null;
  method?: Method;
  endWithSlash?: boolean;
  formattingParams?: FormattingParams;
  customHeader?: Headers;
  customOptions?: Record<string, any>;
  showError?: boolean;
  signal?: AbortSignal;
};

const actionsHandler = (endpointType: EndpointType) => {
  const endpoint: Endpoint = endpointType
    ? endpoints[endpointType]
    : ({} as Endpoint);

  const unAuthenticatedCall = ({
    payload = null,
    customQuery = null,
    customParams = null,
    method = "post",
    endWithSlash = false,
    formattingParams,
    customHeader = {},
  }: UnAuthenticatedCallArgs) => {
    return async (): Promise<ApiResponse | false> => {
      let query = `${endpoint.url}`;
      if (formattingParams && Object.keys(formattingParams)?.length) {
        Object.keys(formattingParams).forEach((param) => {
          if (query.includes(`[${param}]`)) {
            query = query.replace(`[${param}]`, formattingParams[param]);
          }
        });
      }
      const basePath = `${query}${
        customParams
          ? "/" + customParams.join("/") + (endWithSlash ? "/" : "")
          : ""
      }`;
      const prefix = basePath.includes("?") ? "&" : "?";
      query = `${basePath}${customQuery ? prefix + customQuery : ""}`;

      const response = await api(query, payload, method, false, customHeader);
      response.ok = isSuccess(response?.status);
      return response;
      // if (isSuccess(response?.status)) {
      // } else {
      //   return false;
      // }
    };
  };

  const authenticatedCall: any = ({
    payload = null,
    customQuery = null,
    customParams = null,
    method = "post",
    endWithSlash = false,
    formattingParams,
    customHeader = {},
    customOptions = {},
    signal, // Add this parameter
  }: UnAuthenticatedCallArgs) => {
    return async (): Promise<ApiResponse | false> => {
      let query = `${endpoint.url}`;
      if (formattingParams && Object.keys(formattingParams)?.length) {
        Object.keys(formattingParams).forEach((param) => {
          if (query.includes(`[${param}]`)) {
            query = query.replace(`[${param}]`, formattingParams[param]);
          }
        });
      }
      const basePath = `${query}${
        customParams
          ? "/" + customParams.join("/") + (endWithSlash ? "/" : "")
          : ""
      }`;
      const prefix = basePath.includes("?") ? "&" : "?";
      query = `${basePath}${customQuery ? prefix + customQuery : ""}`;

      // Merge signal into customOptions if provided
      const finalOptions = signal
        ? { ...customOptions, signal }
        : customOptions;

      const response = await api(
        query,
        payload,
        method,
        false,
        customHeader,
        false,
        finalOptions,
      );
      response.ok = isSuccess(response?.status);
      return response;
    };
  };

  const getAllData = ({
    page,
    search,
    customQuery = "",
    customParams,
    method = "get",
    payload = {},
    formattingParams,
    signal,
  }: PaginatedPayload) => {
    return async (): Promise<ApiResponse | false> => {
      const basePath = `${endpoint.url}${
        customParams ? "/" + customParams.join("/") : ""
      }`;
      const prefix = basePath.includes("?") ? "&" : "?";
      let query = `${basePath}${prefix}page=${page}${search ? "&search=" + search : ""}${
        customQuery ? `&${customQuery}` : ""
      }`;
      if (formattingParams && Object.keys(formattingParams)?.length) {
        Object.keys(formattingParams).forEach((param) => {
          if (query.includes(`[${param}]`)) {
            query = query.replace(`[${param}]`, formattingParams[param]);
          }
        });
      }
      const blobType = customQuery?.includes("format");
      const customOptions = signal ? { signal } : {};
      const response = await api(
        query,
        payload,
        method,
        false,
        {},
        false,
        customOptions,
        blobType,
      );
      const { ok, data } = response;
      return { ok, data };
    };
  };

  const getAllDataWithLimit = ({
    limit,
    page,
    search,
    customQuery = "",
    customParams,
    method = "get",
    payload = {},
    formattingParams,
    signal,
  }: PaginatedPayload) => {
    return async (): Promise<ApiResponse | false> => {
      const basePath = `${endpoint.url}${
        customParams ? "/" + customParams.join("/") : ""
      }`;
      const prefix = basePath.includes("?") ? "&" : "?";
      let query = `${basePath}${prefix}limit=${limit}&page=${page}${search ? "&search=" + search : ""}${
        customQuery ? `&${customQuery}` : ""
      }`;
      if (formattingParams && Object.keys(formattingParams)?.length) {
        Object.keys(formattingParams).forEach((param) => {
          if (query.includes(`[${param}]`)) {
            query = query.replace(`[${param}]`, formattingParams[param]);
          }
        });
      }
      const blobType = customQuery?.includes("format");
      const customOptions = signal ? { signal } : {};
      const response = await api(
        query,
        payload,
        method,
        false,
        {},
        false,
        customOptions,
        blobType,
      );
      const { ok, data } = response;
      return { ok, data };
    };
  };

  const getAllDataWithoutPagination = ({
    customQuery = "",
    customParams,
    method = "get",
    payload = {},
    search,
    formattingParams,
    signal,
  }: NonPaginatedPayload) => {
    return async (): Promise<ApiResponse | false> => {
      search = search ? "search=" + search : "";
      customQuery = customQuery && search ? "&" + customQuery : customQuery;
      const basePath = `${endpoint.url}${
        customParams?.length ? "/" + customParams.join("/") : ""
      }`;
      const prefix = basePath.includes("?") ? "&" : "?";
      let query = `${basePath}${
        search || customQuery ? prefix + search + customQuery : ""
      }`;
      if (formattingParams && Object.keys(formattingParams)?.length) {
        Object.keys(formattingParams).forEach((param) => {
          if (query.includes(`[${param}]`)) {
            query = query.replace(`[${param}]`, formattingParams[param]);
          }
        });
      }
      const blobType = customQuery?.includes("download");
      const customOptions = signal ? { signal } : {};
      const response = await api(
        query,
        payload,
        method,
        false,
        {},
        false,
        customOptions,
        blobType,
      );
      response.ok = isSuccess(response?.status);
      return response;
    };
  };

  const getViewData = (
    id: string,
    customQuery?: QueryParams,
    headers: Headers = {},
    _logOutToken: string | null = null,
  ) => {
    return async (): Promise<ApiResponse | false> => {
      const basePath = `${endpoint.url}${id ? "/" + id : ""}`;
      const prefix = basePath.includes("?") ? "&" : "?";
      const query = `${basePath}${customQuery ? prefix + customQuery : ""}`;
      const response = await api(query, {}, "get", false, headers);
      if (isSuccess(response?.status)) {
        return response?.data;
      } else {
        return false;
      }
    };
  };

  const createData = (
    payload: Payload,
    customQuery?: QueryParams,
    headers: Headers = {},
    manualEndpoint?: string,
    isCompleteManualEndPoint: boolean = false,
    isStream: boolean = false,
    _customOptions: Record<string, any> = {},
  ) => {
    return async (): Promise<ApiResponse | false> => {
      const basePath = manualEndpoint ? manualEndpoint : endpoint.url;
      const prefix = basePath.includes("?") ? "&" : "?";
      const query = `${basePath}${customQuery ? prefix + customQuery : ""}`;
      const response = await api(
        query,
        payload,
        "post",
        false,
        headers,
        isCompleteManualEndPoint,
      );
      if (isSuccess(response?.status)) {
        return isStream ? response : response?.data;
      } else {
        return false;
      }
    };
  };

  const updateData = (payload: Payload, customQuery?: QueryParams) => {
    return async (): Promise<ApiResponse | false> => {
      const basePath = `${endpoint.url}${payload.id ? "/" + payload.id : ""}`;
      const prefix = basePath.includes("?") ? "&" : "?";
      const query = `${basePath}${customQuery ? prefix + customQuery : ""}`;
      const response = await api(query, payload, "put");
      if (isSuccess(response?.status)) {
        return response?.data;
      } else {
        return false;
      }
    };
  };

  const updateDataWithReturn = (
    payload: Payload,
    customQuery?: QueryParams,
  ) => {
    return async (): Promise<ApiResponse | false> => {
      const basePath = `${endpoint.url}${payload.id ? "/" + payload.id : ""}`;
      const prefix = basePath.includes("?") ? "&" : "?";
      const query = `${basePath}${customQuery ? prefix + customQuery : ""}`;
      const response = await api(query, payload, "put");
      // if
      response.ok = isSuccess(response?.status);
      return response;
    };
  };

  const deleteData = (id: string) => {
    return async (): Promise<ApiResponse | false> => {
      const query = `${endpoint.url}/${id}`;
      const response = await api(query, {}, "delete");
      if (isSuccess(response?.status)) {
        return response?.data;
      } else {
        return false;
      }
    };
  };

  return {
    createData,
    getAllData,
    getAllDataWithoutPagination,
    getViewData,
    updateData,
    updateDataWithReturn,
    deleteData,
    unAuthenticatedCall,
    authenticatedCall,
    getAllDataWithLimit,
  };
};

export default actionsHandler;

/* eslint-disable  @typescript-eslint/no-explicit-any */
